import type { DaghfalClient } from '../api/client';
import type { PushConfigResponse } from '../api/types';
import { fromBase64Url, toBase64Url } from '../auth/webauthn';

/**
 * Web Push in the guardian app (Q68, decision 2026-10-14 #7).
 *
 * The browser keeps the subscription; the API keeps a copy of it as a device, tied to whoever was signed in when
 * it was made. Phones are shared, so signing out removes both before the session goes ({@link forgetPush}), and
 * the next person on the phone turns push on for themselves.
 */

/** Where the service worker is served from: `apps/guardian/public/sw.js`. */
export const PUSH_WORKER_URL = '/sw.js';

/** How the app tells the API which app a subscription belongs to: only what reaches that app is pushed. */
const APP = 'guardian';

/** What this browser can do about push, and why not where it cannot. */
export type PushSupport = 'supported' | 'ios-install' | 'unsupported';

/**
 * Whether this browser can subscribe. An iPhone or iPad pushes only to a web app added to the home screen
 * (iOS 16.4 and later), and Safari outside one has no `PushManager` at all: that case is told how to add it.
 */
export function pushSupport(): PushSupport {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return 'unsupported';
  if ('serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window) return 'supported';
  return isAppleMobile() && !isStandalone() ? 'ios-install' : 'unsupported';
}

function isAppleMobile(): boolean {
  // An iPad asks for the desktop site and calls itself a Mac, with a touch screen.
  return /iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

function isStandalone(): boolean {
  return window.matchMedia?.('(display-mode: standalone)').matches === true
    || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

/** Registers the worker that shows a push. Called once at start-up by the guardian app; harmless where unsupported. */
export function registerPushWorker(): void {
  if (pushSupport() !== 'supported') return;
  void navigator.serviceWorker.register(PUSH_WORKER_URL).catch(() => {
    // Without the worker nothing is pushed, and the notification centre still has everything.
  });
}

async function registration(): Promise<ServiceWorkerRegistration | null> {
  if (pushSupport() !== 'supported') return null;
  return (await navigator.serviceWorker.getRegistration(PUSH_WORKER_URL))
    ?? navigator.serviceWorker.register(PUSH_WORKER_URL);
}

/** This browser's subscription, where it has one. */
export async function currentSubscription(): Promise<PushSubscription | null> {
  const worker = await registration();
  return worker ? worker.pushManager.getSubscription() : null;
}

function deviceOf(subscription: PushSubscription) {
  const p256dh = subscription.getKey('p256dh');
  const auth = subscription.getKey('auth');
  return {
    token: subscription.endpoint,
    devicePlatform: 'web',
    appVersion: null,
    p256dh: p256dh ? toBase64Url(p256dh) : null,
    auth: auth ? toBase64Url(auth) : null,
    app: APP,
  };
}

/** Why turning push on did not finish. */
export class PushRefusedError extends Error {
  constructor(readonly reason: 'denied' | 'unsupported' | 'off') {
    super(`Push was not turned on: ${reason}.`);
    this.name = 'PushRefusedError';
  }
}

/**
 * Asks the browser for permission, subscribes with the deployment's key, and tells the API. Must be called from a
 * tap: browsers refuse a permission prompt nobody asked for. A caller already holding the deployment's push
 * configuration passes it, and the API is not asked again.
 */
export async function enablePush(client: DaghfalClient, known?: PushConfigResponse): Promise<PushSubscription> {
  const config = known ?? await client.notifications.pushConfig();
  if (!config.enabled || !config.publicKey) throw new PushRefusedError('off');

  const worker = await registration();
  if (!worker) throw new PushRefusedError('unsupported');

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new PushRefusedError('denied');

  const subscription = (await worker.pushManager.getSubscription())
    ?? await worker.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: fromBase64Url(config.publicKey),
    });

  await client.notifications.registerDevice(deviceOf(subscription));
  return subscription;
}

/** Stops pushing to this browser: the API forgets the device, then the browser drops the subscription. */
export async function disablePush(client: DaghfalClient): Promise<void> {
  const subscription = await currentSubscription();
  if (!subscription) return;
  try {
    await client.notifications.removeDevice(subscription.endpoint);
  } finally {
    await subscription.unsubscribe();
  }
}

/**
 * Sends the subscription again on each app open, so the API's copy follows a browser that renewed it. Does
 * nothing where push was never turned on, or the permission was taken back.
 */
export async function refreshPush(client: DaghfalClient): Promise<void> {
  if (pushSupport() !== 'supported' || Notification.permission !== 'granted') return;
  const subscription = await currentSubscription();
  if (subscription) await client.notifications.registerDevice(deviceOf(subscription));
}

/**
 * Called on sign-out, before the tokens go: the next person on a shared phone must not be pushed this one's
 * notifications. Never throws, and gives up after a few seconds, so a dead connection cannot keep anyone signed in.
 */
export async function forgetPush(client: DaghfalClient, timeoutMs = 4000): Promise<void> {
  if (client.appName !== APP || pushSupport() !== 'supported') return;
  await Promise.race([
    disablePush(client).catch(() => undefined),
    new Promise<void>((resolve) => setTimeout(resolve, timeoutMs)),
  ]);
}
